import { useEffect, useMemo, useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Trophy,
  Banknote,
  Percent,
  MapPin,
  Loader2,
  Calendar,
  Users,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { toUserFriendlyError } from "@/lib/error-messages";

const BRAZIL_STATES = [
  "AC", "AL", "AP", "AM", "BA", "CE", "DF", "ES", "GO", "MA", "MT", "MS",
  "MG", "PA", "PB", "PR", "PE", "PI", "RJ", "RN", "RS", "RO", "RR", "SC", "SP", "SE", "TO",
];

export default function SellerProfileSheet({
  open,
  onOpenChange,
  /** Se definido, edita esse userId; senão, promove um usuário existente (com select). */
  userId: initialUserId,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  userId?: number | null;
  onSaved?: (userId: number) => void;
}) {
  const editing = !!initialUserId;

  const [selectedUserId, setSelectedUserId] = useState<number | null>(
    initialUserId ?? null,
  );
  const [monthlyGoal, setMonthlyGoal] = useState("0");
  const [commissionPct, setCommissionPct] = useState("0");
  const [territories, setTerritories] = useState<string[]>([]);
  const [joinedAt, setJoinedAt] = useState<string>("");
  const [isActive, setIsActive] = useState(true);
  const [notes, setNotes] = useState("");
  // "" = sem gerente. Guardado como string porque é o valor de um <Select>.
  const [managerUserId, setManagerUserId] = useState<string>("");

  useEffect(() => {
    if (open) {
      setSelectedUserId(initialUserId ?? null);
    }
  }, [open, initialUserId]);

  const availableUsers = trpc.crm.sellers.usersWithoutProfile.useQuery(undefined, {
    enabled: open && !editing,
  });

  const profileQuery = trpc.crm.sellers.get.useQuery(
    editing && initialUserId
      ? { userId: initialUserId, period: { kind: "month" as const } }
      : (undefined as any),
    { enabled: open && editing && !!initialUserId },
  );

  useEffect(() => {
    if (!open) return;
    if (editing && profileQuery.data) {
      const p = profileQuery.data.profile;
      setMonthlyGoal(String(p.monthlyGoal ?? "0"));
      setCommissionPct(String(p.commissionPct ?? "0"));
      setTerritories((p.territories as string[]) ?? []);
      setJoinedAt(p.joinedAt ? new Date(p.joinedAt).toISOString().slice(0, 10) : "");
      setIsActive(p.isActive);
      setNotes(p.notes ?? "");
      setManagerUserId(p.managerUserId ? String(p.managerUserId) : "");
    } else if (!editing) {
      setMonthlyGoal("0");
      setCommissionPct("0");
      setTerritories([]);
      setJoinedAt(new Date().toISOString().slice(0, 10));
      setIsActive(true);
      setNotes("");
      setManagerUserId("");
    }
  }, [open, editing, profileQuery.data]);

  // Candidatos a gerente: os demais vendedores do tenant. Um vendedor não pode
  // gerenciar a si mesmo (o servidor também rejeita).
  const sellersQuery = trpc.crm.sellers.list.useQuery(
    { includeInactive: true },
    { enabled: open },
  );
  const managerOptions = useMemo(
    () =>
      (sellersQuery.data ?? []).filter(
        (s: any) => s.profile.userId !== selectedUserId,
      ),
    [sellersQuery.data, selectedUserId],
  );

  const utils = trpc.useUtils();
  const upsert = trpc.crm.sellers.upsert.useMutation({
    onSuccess: () => {
      toast.success(editing ? "Perfil atualizado" : "Vendedor adicionado");
      utils.crm.sellers.list.invalidate();
      utils.crm.sellers.ranking.invalidate();
      utils.crm.sellers.usersWithoutProfile.invalidate();
      if (selectedUserId) {
        utils.crm.sellers.get.invalidate({
          userId: selectedUserId,
          period: { kind: "month" as const },
        });
        onSaved?.(selectedUserId);
      }
      onOpenChange(false);
    },
    onError: (e) => toast.error(toUserFriendlyError(e, "Erro ao salvar perfil do vendedor")),
  });

  function toggleTerritory(uf: string) {
    setTerritories((prev) =>
      prev.includes(uf) ? prev.filter((x) => x !== uf) : [...prev, uf],
    );
  }

  function submit() {
    if (!selectedUserId) return toast.error("Selecione um usuário");
    const pct = Number(commissionPct);
    if (Number.isNaN(pct) || pct < 0 || pct > 100) {
      return toast.error("Comissão deve estar entre 0 e 100");
    }
    upsert.mutate({
      userId: selectedUserId,
      monthlyGoal: monthlyGoal || "0",
      commissionPct: commissionPct || "0",
      territories: territories.length > 0 ? territories : null,
      joinedAt: joinedAt ? new Date(joinedAt) : null,
      isActive,
      notes: notes || null,
      managerUserId: managerUserId ? Number(managerUserId) : null,
    });
  }

  const users = availableUsers.data ?? [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-[480px] p-0 flex flex-col">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Trophy className="h-5 w-5" />
            </div>
            <div>
              <SheetTitle>
                {editing ? "Perfil do vendedor" : "Adicionar vendedor"}
              </SheetTitle>
              <SheetDescription>
                {editing
                  ? "Atualize meta, comissão e territórios."
                  : "Promova um usuário a vendedor com meta e comissão."}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {!editing && (
            <div>
              <Label className="text-xs">Usuário</Label>
              {availableUsers.isLoading ? (
                <div className="h-9 mt-1 rounded-md bg-muted animate-pulse" />
              ) : users.length === 0 ? (
                <p className="text-sm text-muted-foreground mt-1">
                  Todos os usuários ativos já são vendedores.
                </p>
              ) : (
                <Select
                  value={selectedUserId ? String(selectedUserId) : ""}
                  onValueChange={(v) => setSelectedUserId(Number(v))}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione um usuário" />
                  </SelectTrigger>
                  <SelectContent>
                    {users.map((u: any) => (
                      <SelectItem key={u.id} value={String(u.id)}>
                        {u.name ?? u.email}
                        {u.jobTitle && (
                          <span className="text-muted-foreground"> · {u.jobTitle}</span>
                        )}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          )}

          {/* ── Meta + comissão ───────────────────── */}
          <section className="space-y-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Metas & comissão
            </Label>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Meta mensal (R$)</Label>
                <div className="relative mt-1">
                  <Banknote className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input
                    type="number"
                    step="0.01"
                    value={monthlyGoal}
                    onChange={(e) => setMonthlyGoal(e.target.value)}
                    className="pl-8 tabular-nums"
                  />
                </div>
              </div>
              <div>
                <Label className="text-xs">Comissão (%)</Label>
                <div className="relative mt-1">
                  <Percent className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input
                    type="number"
                    step="0.01"
                    min={0}
                    max={100}
                    value={commissionPct}
                    onChange={(e) => setCommissionPct(e.target.value)}
                    className="pl-8 tabular-nums"
                  />
                </div>
              </div>
            </div>
          </section>

          <Separator />

          {/* ── Hierarquia ────────────────────────── */}
          <section className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <Users className="h-3 w-3" />
              Gerente comercial
            </Label>
            <p className="text-xs text-muted-foreground">
              Quem gerencia este vendedor pode repassar as oportunidades dele.
              Deixe em branco se ele não responde a ninguém.
            </p>
            <Select
              value={managerUserId || "none"}
              onValueChange={(v) => setManagerUserId(v === "none" ? "" : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sem gerente" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sem gerente</SelectItem>
                {managerOptions.map((s: any) => (
                  <SelectItem key={s.profile.userId} value={String(s.profile.userId)}>
                    {s.user.name ?? s.user.email}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </section>

          <Separator />

          {/* ── Territórios ───────────────────────── */}
          <section className="space-y-2">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
              <MapPin className="h-3 w-3" />
              Territórios (UFs)
            </Label>
            <p className="text-xs text-muted-foreground">
              Regiões atendidas pelo vendedor. Deixe vazio se atende nacional.
            </p>
            {territories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {territories.map((uf) => (
                  <Badge
                    key={uf}
                    variant="secondary"
                    className="gap-1 pl-2 pr-1 py-0.5"
                  >
                    {uf}
                    <button
                      onClick={() => toggleTerritory(uf)}
                      className="hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="grid grid-cols-6 sm:grid-cols-9 gap-1">
              {BRAZIL_STATES.map((uf) => {
                const active = territories.includes(uf);
                return (
                  <button
                    key={uf}
                    type="button"
                    onClick={() => toggleTerritory(uf)}
                    className={`h-7 rounded-md text-[11px] font-medium transition ${
                      active
                        ? "bg-primary text-primary-foreground shadow-sm"
                        : "bg-muted hover:bg-muted/70 text-muted-foreground"
                    }`}
                  >
                    {uf}
                  </button>
                );
              })}
            </div>
          </section>

          <Separator />

          {/* ── Meta info ─────────────────────────── */}
          <section className="space-y-3">
            <div>
              <Label className="text-xs flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                Data de entrada
              </Label>
              <Input
                type="date"
                value={joinedAt}
                onChange={(e) => setJoinedAt(e.target.value)}
                className="mt-1"
              />
            </div>
            <div className="flex items-center justify-between rounded-md border px-3 py-2">
              <div>
                <div className="text-sm font-medium">Vendedor ativo</div>
                <div className="text-xs text-muted-foreground">
                  Desative para excluir do ranking e round-robin.
                </div>
              </div>
              <Switch checked={isActive} onCheckedChange={setIsActive} />
            </div>
            <div>
              <Label className="text-xs">Observações</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                placeholder="Opcional"
                className="mt-1"
              />
            </div>
          </section>
        </div>

        <SheetFooter className="border-t px-6 py-4 bg-muted/30">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={submit} disabled={upsert.isPending || !selectedUserId}>
            {upsert.isPending && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
            {editing ? "Salvar" : "Adicionar vendedor"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
